import React from 'react';
import ReactMarkdown from 'react-markdown';
import startCase from 'lodash/startCase';
import toLower from 'lodash/toLower';
import { RiArrowDownSLine, RiArrowUpSLine } from '@remixicon/react';
import Tag, { TagVariant } from '../../atoms/Tag';
import components from '../Markdown/components';
import { translate } from '../../../localization';
import styles from './BrandsTable.module.css';
import useTruncatableText from '../../../hooks/useTruncatableText';
import NoDataInline, { NoDataInlineVariant } from '../../atoms/NoDataInline';
import { Citation } from '../../../types/Content';

type BrandsInfoTableCellProps = {
  rowValue: string | Citation[] | React.ReactNode;
  columnName: string;
};

const BrandsInfoTableCell = ({
  rowValue,
  columnName,
}: BrandsInfoTableCellProps) => {
  const { textRef, isExpanded, isTruncated, toggleExpansion } =
    useTruncatableText();

  const [INCLUSIONS, EXCLUSIONS, CITATION, CLAIM_TEXT, CLAIM_DESCRIPTION] = [
    'inclusions',
    'exclusions',
    'citation',
    'claimText',
    'claimDescription',
  ];

  const isInclusionOrExclusion =
    columnName === INCLUSIONS || columnName === EXCLUSIONS;

  const isCitation = columnName === CITATION;

  const isClaimsColumn = columnName === CLAIM_TEXT;

  const isClaimDescription = columnName === CLAIM_DESCRIPTION;

  const rowValueArray =
    isInclusionOrExclusion && typeof rowValue === 'string'
      ? rowValue.split(', ')
      : [];

  const hasNonFalsyValue = (arr: (string | null | undefined)[]): boolean =>
    arr.some(value => Boolean(value) && value?.trim() !== '');

  const renderTagListForInclusionExclusions = () => (
    <div
      className={`${styles['brands-table__overflow-text-container']} ${
        isExpanded
          ? styles['brands-table__overflow-text-container--expanded']
          : ''
      }`}
      ref={textRef}
    >
      {rowValueArray?.map(val => (
        <span
          key={val}
          className={styles['brands-table__inclusions-exclusions']}
        >
          <Tag
            variant={TagVariant.DEFAULT}
            icon={null}
            text={startCase(toLower(val))}
            classes={styles['brands-table__inclusions-exclusions-tag']}
          />
        </span>
      ))}
    </div>
  );

  const renderTagListWithToggle = () => (
    <>
      {/* Show the toggle button only if truncation is applied or text is expanded */}
      {renderTagListForInclusionExclusions()}
      {(isTruncated || isExpanded) && (
        <button onClick={toggleExpansion} className={styles.showMoreLessButton}>
          {isExpanded
            ? translate('show_less')
            : `${translate('show_all')} (${rowValueArray?.length})`}

          {isExpanded ? (
            <RiArrowUpSLine
              className={styles.showMoreLessIcon}
              color='#0000C9'
              size={14}
            />
          ) : (
            <RiArrowDownSLine
              className={styles.showMoreLessIcon}
              color='#0000C9'
              size={14}
            />
          )}
        </button>
      )}
    </>
  );

  const renderCitationWithToggle = () => {
    return (
      <>
        <div
          className={`${styles['brands-table__overflow-text-container']} ${
            styles['brands-table__overflow-text-container__citation']
          } ${
            isExpanded
              ? styles['brands-table__overflow-text-container--expanded']
              : ''
          }`}
          ref={textRef}
        >
          {(rowValue as Citation[])?.map((citation, index) => (
            <div
              className={styles['brands-table__citation-item-wrapper']}
              key={`${citation?.text?.substring(0, 5)} ${index + 1}}`}
            >
              {citation?.text && (
                <span className={styles['brands-table__citation-text']}>
                  {index + 1}. {citation?.text}
                </span>
              )}

              {citation?.location && (
                <span className={styles['brands-table__citation-location']}>
                  {citation?.location}
                </span>
              )}
            </div>
          ))}
        </div>

        {(isTruncated || isExpanded) && (
          <button
            onClick={toggleExpansion}
            className={`${styles.showMoreLessButtonForCitations} escape-copy`}
          >
            {isExpanded
              ? translate('show_less')
              : `${translate('show_all_citations')} (${
                  (rowValue as Citation[])?.length
                })`}

            {isExpanded ? (
              <RiArrowUpSLine
                className={styles.showMoreLessIcon}
                color='#0000C9'
                size={14}
              />
            ) : (
              <RiArrowDownSLine
                className={styles.showMoreLessIcon}
                color='#0000C9'
                size={14}
              />
            )}
          </button>
        )}
      </>
    );
  };

  const renderClaimDescriptionWithToggle = () => {
    // For JSX content (like claim descriptions with embedded links),
    // render directly without truncation since they're typically short
    const isJSXContent = React.isValidElement(rowValue);

    if (isJSXContent) {
      return (
        <div className={styles['brands-table__claims-column-content']}>
          {rowValue}
        </div>
      );
    }

    // For string content, use the full truncation logic
    return (
      <>
        <div
          className={`${styles['brands-table__overflow-text-container']} ${
            styles['brands-table__claims-column-content']
          } ${
            isExpanded
              ? styles['brands-table__overflow-text-container--expanded']
              : ''
          }`}
          ref={textRef}
        >
          {typeof rowValue === 'string' ? rowValue : String(rowValue)}
        </div>

        {(isTruncated || isExpanded) && (
          <button
            onClick={toggleExpansion}
            className={`${styles.showMoreLessButton} ${styles.showMoreLessButtonForCitations} escape-copy`}
          >
            {isExpanded ? translate('show_less') : translate('show_more')}

            {isExpanded ? (
              <RiArrowUpSLine
                className={styles.showMoreLessIcon}
                color='#0000C9'
                size={14}
              />
            ) : (
              <RiArrowDownSLine
                className={styles.showMoreLessIcon}
                color='#0000C9'
                size={14}
              />
            )}
          </button>
        )}
      </>
    );
  };

  const renderClaimsWithToggle = () => (
    <>
      <div
        className={`${styles['brands-table__overflow-text-container']} ${
          styles['brands-table__claims-column-content']
        } ${
          isExpanded
            ? styles['brands-table__overflow-text-container--expanded']
            : ''
        }`}
        ref={textRef}
      >
        {rowValue as string}
      </div>

      {(isTruncated || isExpanded) && (
        <button
          onClick={toggleExpansion}
          className={`${styles.showMoreLessButton} ${styles.showMoreLessButtonForCitations} escape-copy`}
        >
          {isExpanded ? translate('show_more') : translate('show_more')}

          {isExpanded ? (
            <RiArrowUpSLine
              className={styles.showMoreLessIcon}
              color='#0000C9'
              size={14}
            />
          ) : (
            <RiArrowDownSLine
              className={styles.showMoreLessIcon}
              color='#0000C9'
              size={14}
            />
          )}
        </button>
      )}
    </>
  );

  const renderNoData = () => (
    <NoDataInline variant={NoDataInlineVariant.MEDIUM_TABLE_FIELD} />
  );

  if (isClaimDescription) {
    return rowValue ? renderClaimDescriptionWithToggle() : renderNoData();
  }

  if (isInclusionOrExclusion) {
    return rowValueArray?.length > 0 && hasNonFalsyValue(rowValueArray)
      ? renderTagListWithToggle()
      : renderNoData();
  }

  if (isCitation) {
    return Array.isArray(rowValue) && rowValue?.length > 0
      ? renderCitationWithToggle()
      : renderNoData();
  }

  if (isClaimsColumn) {
    return rowValue &&
      typeof rowValue === 'string' &&
      rowValue?.length > 0 &&
      rowValue?.trim() !== ''
      ? renderClaimsWithToggle()
      : renderNoData();
  }

  return (
    <ReactMarkdown components={components}>{rowValue as string}</ReactMarkdown>
  );
};

export default BrandsInfoTableCell;
